import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

const GUEST_CART_KEY = "leestone_guest_cart";

export interface VariantInfo {
  id: string;
  sku?: string;
  price_adjustment: number;
  options: { type_name: string; option_value: string }[];
  image_url?: string;
}

interface GuestCartItem {
  product_id: string;
  quantity: number;
  variant_id?: string;
  variant_info?: VariantInfo;
}

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  variant_id?: string;
  variant_info?: VariantInfo;
  product: {
    id: string;
    name: string;
    slug: string;
    price: number;
    images: string[];
  };
}

interface CartContextType {
  items: CartItem[];
  loading: boolean;
  itemCount: number;
  subtotal: number;
  addToCart: (productId: string, quantity?: number, variantId?: string, variantInfo?: VariantInfo) => Promise<void>;
  updateQuantity: (productId: string, quantity: number, variantId?: string, itemId?: string) => Promise<void>;
  removeFromCart: (productId: string, variantId?: string, itemId?: string) => Promise<void>;
  clearCart: (skipStockRelease?: boolean) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Helper functions for localStorage guest cart
function getGuestCart(): GuestCartItem[] {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function setGuestCart(items: GuestCartItem[]): void {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
}

function clearGuestCartStorage(): void {
  localStorage.removeItem(GUEST_CART_KEY);
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch product details for guest cart items
  const fetchProductDetails = useCallback(async (guestItems: GuestCartItem[]): Promise<CartItem[]> => {
    if (guestItems.length === 0) return [];

    const productIds = guestItems.map((item) => item.product_id);
    const { data: products, error } = await supabase
      .from("products")
      .select("id, name, slug, price, images")
      .in("id", productIds);

    if (error || !products) {
      console.error("Error fetching products for guest cart:", error);
      return [];
    }

    return guestItems
      .map((guestItem, index) => {
        const product = products.find((p) => p.id === guestItem.product_id);
        if (!product) return null;
        return {
          id: `guest-${guestItem.product_id}-${guestItem.variant_id || 'no-variant'}-${index}`,
          product_id: guestItem.product_id,
          quantity: guestItem.quantity,
          variant_id: guestItem.variant_id,
          variant_info: guestItem.variant_info,
          product: {
            id: product.id,
            name: product.name,
            slug: product.slug,
            price: product.price,
            images: product.images || [],
          },
        };
      })
      .filter((item): item is CartItem => item !== null);
  }, []);

  // Merge guest cart with user cart on sign-in
  const mergeGuestCartToUser = useCallback(async () => {
    const guestItems = getGuestCart();
    if (guestItems.length === 0 || !user) return;

    for (const guestItem of guestItems) {
      // Check if product already in user's cart
      const { data: existingItem } = await supabase
        .from("cart_items")
        .select("id, quantity")
        .eq("user_id", user.id)
        .eq("product_id", guestItem.product_id)
        .single();

      if (existingItem) {
        // Update quantity
        await supabase
          .from("cart_items")
          .update({ quantity: existingItem.quantity + guestItem.quantity })
          .eq("id", existingItem.id);
      } else {
        // Insert new item
        await supabase.from("cart_items").insert({
          user_id: user.id,
          product_id: guestItem.product_id,
          quantity: guestItem.quantity,
        });
      }
    }

    // Clear guest cart after merge
    clearGuestCartStorage();

    toast({
      title: "Cart synced",
      description: "Your items have been added to your account cart.",
    });
  }, [user, toast]);

  // Fetch cart based on authentication state
  const fetchCart = useCallback(async () => {
    setLoading(true);

    if (user) {
      // Authenticated user - fetch from database
      // Get cart items including variant_id
      const { data: cartData, error: cartError } = await supabase
        .from("cart_items")
        .select("id, product_id, quantity, variant_id")
        .eq("user_id", user.id);

      if (cartError) {
        console.error("Error fetching cart:", cartError);
        setLoading(false);
        return;
      }

      if (!cartData || cartData.length === 0) {
        setItems([]);
        setLoading(false);
        return;
      }

      // Fetch the product details
      const productIds = cartData.map(item => item.product_id);
      const { data: products, error: productsError } = await supabase
        .from("products")
        .select("id, name, slug, price, images")
        .in("id", productIds);

      if (productsError) {
        console.error("Error fetching products:", productsError);
        setLoading(false);
        return;
      }

      // Fetch variant details if any cart items have variants
      const variantIds = cartData.filter(item => item.variant_id).map(item => item.variant_id);
      const variantsMap: Record<string, VariantInfo> = {};
      
      if (variantIds.length > 0) {
        const { data: variants } = await supabase
          .from("product_variants")
          .select(`
            id,
            sku,
            price_adjustment,
            image_url,
            product_variant_options (
              variant_options (
                value,
                variant_types (
                  name
                )
              )
            )
          `)
          .in("id", variantIds);

        if (variants) {
          for (const v of variants) {
            variantsMap[v.id] = {
              id: v.id,
              sku: v.sku || undefined,
              price_adjustment: Number(v.price_adjustment) || 0,
              image_url: v.image_url || undefined,
              options: (v.product_variant_options as any[])?.map((pvo: any) => ({
                type_name: pvo.variant_options?.variant_types?.name || '',
                option_value: pvo.variant_options?.value || '',
              })) || [],
            };
          }
        }
      }

      // Combine cart items with product details and variant info
      const itemsWithProducts: CartItem[] = cartData
        .map(cartItem => {
          const product = products?.find(p => p.id === cartItem.product_id);
          if (!product) return null;
          return {
            id: cartItem.id,
            product_id: cartItem.product_id,
            quantity: cartItem.quantity,
            variant_id: cartItem.variant_id || undefined,
            variant_info: cartItem.variant_id ? variantsMap[cartItem.variant_id] : undefined,
            product: {
              id: product.id,
              name: product.name,
              slug: product.slug,
              price: product.price,
              images: product.images || [],
            },
          };
        })
        .filter((item): item is CartItem => item !== null);

      setItems(itemsWithProducts);
    } else {
      // Guest user - fetch from localStorage and get product details
      const guestItems = getGuestCart();
      const cartWithProducts = await fetchProductDetails(guestItems);
      setItems(cartWithProducts);
    }

    setLoading(false);
  }, [user, fetchProductDetails]);

  // Handle user authentication changes
  useEffect(() => {
    if (user) {
      // User just signed in - merge guest cart then fetch
      mergeGuestCartToUser().then(() => fetchCart());
    } else {
      // Guest or signed out - load guest cart
      fetchCart();
    }
  }, [user, fetchCart, mergeGuestCartToUser]);

  const addToCart = async (productId: string, quantity: number = 1, variantId?: string, variantInfo?: VariantInfo) => {
    // Helper to match cart item by product + variant (handle null/undefined equivalence)
    const matchItem = (item: CartItem) => {
      if (item.product_id !== productId) return false;
      const itemVariant = item.variant_id ?? null;
      const targetVariant = variantId ?? null;
      return itemVariant === targetVariant;
    };

    // Optimistic update
    setItems((prevItems) => {
      const existingItem = prevItems.find(matchItem);
      if (existingItem) {
        return prevItems.map((item) =>
          matchItem(item)
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return prevItems;
    });

    if (user) {
      // Authenticated user — use stock reservation RPC
      const existingItem = items.find(matchItem);
      if (existingItem) {
         await updateQuantity(productId, existingItem.quantity + quantity, variantId);
      } else {
         // Reserve stock via RPC
         const { data: reserveResult, error: reserveError } = await supabase.rpc('reserve_cart_stock', {
           p_user_id: user.id,
           p_product_id: productId,
           p_quantity: quantity,
           p_variant_id: variantId || null,
         });

         if (reserveError || (reserveResult && !reserveResult.success)) {
           toast({
             title: "Stock Unavailable",
             description: reserveResult?.message || reserveError?.message || "Could not reserve stock",
             variant: "destructive",
           });
           return;
         }

         toast({
           title: "Added to cart",
           description: "Item has been added to your cart",
           action: (
             <ToastAction altText="View Cart" onClick={() => window.location.href = '/cart'}>
               View Cart
             </ToastAction>
           ),
         });
         fetchCart();
      }
    } else {
      // Guest logic with variant support
      const guestItems = getGuestCart();
      const existingIndex = guestItems.findIndex((item) => 
        item.product_id === productId && item.variant_id === variantId
      );

      if (existingIndex >= 0) {
        guestItems[existingIndex].quantity += quantity;
      } else {
        guestItems.push({ 
          product_id: productId, 
          quantity,
          variant_id: variantId,
          variant_info: variantInfo,
        });
      }

      setGuestCart(guestItems);
      toast({
        title: "Added to cart",
        description: "Item has been added to your cart",
        action: (
          <ToastAction altText="View Cart" onClick={() => window.location.href = '/cart'}>
            View Cart
          </ToastAction>
        ),
      });
      fetchCart();
    }
  };

  const updateQuantity = async (productId: string, quantity: number, variantId?: string, itemId?: string) => {
    if (quantity <= 0) {
      await removeFromCart(productId, variantId, itemId);
      return;
    }

    // For authenticated users with itemId, use ID-based matching (most reliable)
    if (user && itemId) {
      const oldItems = [...items];
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, quantity } : item
        )
      );

      const { error } = await supabase
        .from("cart_items")
        .update({ quantity })
        .eq("id", itemId)
        .eq("user_id", user.id);

      if (error) {
        setItems(oldItems);
        toast({
          title: "Error",
          description: "Failed to update quantity",
          variant: "destructive",
        });
      }
      return;
    }

    // Fallback: match by product_id + variant_id
    const matchItem = (item: CartItem) => {
      if (item.product_id !== productId) return false;
      const itemVariant = item.variant_id ?? null;
      const targetVariant = variantId ?? null;
      return itemVariant === targetVariant;
    };

    const oldItems = [...items];
    setItems((prev) =>
      prev.map((item) =>
        matchItem(item) ? { ...item, quantity } : item
      )
    );

    if (user) {
      let query = supabase
        .from("cart_items")
        .update({ quantity })
        .eq("user_id", user.id)
        .eq("product_id", productId);
      
      if (variantId) {
        query = query.eq("variant_id", variantId);
      }

      const { error } = await query;

      if (error) {
        setItems(oldItems);
        toast({
          title: "Error",
          description: "Failed to update quantity",
          variant: "destructive",
        });
      }
    } else {
      const guestItems = getGuestCart();
      const existingIndex = guestItems.findIndex((item) => 
        item.product_id === productId && (item.variant_id ?? null) === (variantId ?? null)
      );

      if (existingIndex >= 0) {
        guestItems[existingIndex].quantity = quantity;
        setGuestCart(guestItems);
        fetchCart(); 
      }
    }
  };

  const removeFromCart = async (productId: string, variantId?: string, itemId?: string) => {
    // For authenticated users with itemId, use ID-based matching (most reliable)
    if (user && itemId) {
      // Optimistic update - filter by id
      const oldItems = [...items];
      const removedItem = items.find((item) => item.id === itemId);
      const newItems = items.filter((item) => item.id !== itemId);
      setItems(newItems);
      
      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("id", itemId)
        .eq("user_id", user.id);

      if (error) {
        setItems(oldItems);
        toast({
          title: "Error",
          description: "Failed to remove item",
          variant: "destructive",
        });
      } else {
        // Release reserved stock back
        if (removedItem) {
          await supabase.rpc('release_cart_stock', {
            p_user_id: user.id,
            p_product_id: removedItem.product_id,
            p_quantity: removedItem.quantity,
            p_variant_id: removedItem.variant_id || null,
          });
        }
        toast({
          title: "Removed",
          description: "Item removed from cart",
        });
      }
      return;
    }

    // Fallback: match by product_id + variant_id (for guest users or backward compatibility)
    const matchItem = (item: CartItem) => {
      if (item.product_id !== productId) return false;
      const itemVariant = item.variant_id ?? null;
      const targetVariant = variantId ?? null;
      return itemVariant === targetVariant;
    };

    // Optimistic update
    const oldItems = [...items];
    setItems((prev) => prev.filter((item) => !matchItem(item)));

    if (user) {
      let query = supabase
        .from("cart_items")
        .delete()
        .eq("user_id", user.id)
        .eq("product_id", productId);
      
      if (variantId) {
        query = query.eq("variant_id", variantId);
      }

      const { error } = await query;

      if (error) {
        console.error("Error removing from cart:", error);
        setItems(oldItems);
        toast({
          title: "Error",
          description: "Failed to remove item",
          variant: "destructive",
        });
      } else {
        // Release reserved stock back
        const removedItem = oldItems.find(matchItem);
        if (removedItem) {
          await supabase.rpc('release_cart_stock', {
            p_user_id: user.id,
            p_product_id: removedItem.product_id,
            p_quantity: removedItem.quantity,
            p_variant_id: removedItem.variant_id || null,
          });
        }
        toast({
          title: "Removed",
          description: "Item removed from cart",
        });
      }
    } else {
      const guestItems = getGuestCart();
      const filteredItems = guestItems.filter((item) => 
        !(item.product_id === productId && (item.variant_id ?? null) === (variantId ?? null))
      );
      setGuestCart(filteredItems);
      toast({
        title: "Removed",
        description: "Item removed from cart",
      });
      setTimeout(() => fetchCart(), 0); 
    }
  };

  const clearCart = async (skipStockRelease: boolean = false) => {
    if (user) {
      // Release stock for all items before clearing (unless order was just placed)
      if (!skipStockRelease) {
        for (const item of items) {
          await supabase.rpc('release_cart_stock', {
            p_user_id: user.id,
            p_product_id: item.product_id,
            p_quantity: item.quantity,
            p_variant_id: item.variant_id || null,
          });
        }
      }

      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("user_id", user.id);

      if (!error) {
        setItems([]);
      }
    } else {
      clearGuestCartStorage();
      setItems([]);
    }
  };

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce(
    (sum, item) => {
      const basePrice = item.product?.price || 0;
      const adjustment = item.variant_info?.price_adjustment || 0;
      return sum + (basePrice + adjustment) * item.quantity;
    },
    0
  );

  return (
    <CartContext.Provider
      value={{
        items,
        loading,
        itemCount,
        subtotal,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}